import React, { useState, type FC } from 'react';
import { Flex, Popconfirm, Select, Table, type TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { format, isValid, parse } from 'date-fns';
import { Text, Space, Link, Button } from '@g2u/ui-kit';
import { Link as RouteLink } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { TechnicianNoteModal } from 'components';
import { NoteLinkType, TechnicianNote, TehcnicianNoteLink } from 'types';
import { useTechnicianNotes } from '../hooks/useTechnicianNotes';
import he from 'he';
import styles from '../edit.module.scss';

export namespace TechnicianNotes {
  export type Props = Readonly<{
    technicianId?: string;
  }>;

  export const $: FC<Props> = ({ technicianId }) => {
    const [page, setPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [noteType, setNoteType] = useState<string>('');
    const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
    const [editedNote, setEditedNote] = useState<TechnicianNote | null>(null);

    const { notes, noteTypes, isLoading, deleteNote, createNote, updateNote, linkOptions } = useTechnicianNotes(
      page,
      recordsPerPage,
      technicianId,
      noteType,
    );

    const handleTableChange = (newPage: number, newPageSize?: number) => {
      setPage(newPage);
      setRecordsPerPage(newPageSize ?? recordsPerPage);
    };
    const getLinkUrl = (id: number, type: NoteLinkType) => {
      if (type === NoteLinkType.Job) {
        return `/job/edit/${id}/summary`;
      }
      if (type === NoteLinkType.Customer) {
        return `/customer/edit/${id}/details`;
      }
      return '';
    };

    const columns: ColumnsType<any> = [
      {
        key: 'createdTime',
        title: 'Created',
        dataIndex: 'datetime',
        render: (datetime: string) => {
          const parsedDate = parse(datetime, 'yyyy-MM-dd HH:mm:ss', new Date());
          return isValid(parsedDate) ? format(parsedDate, 'dd MMM yyyy HH:mm:ss') : '';
        },
        width: 160,
      },
      {
        key: 'createdBy',
        title: 'Created By',
        dataIndex: 'createdByName',
        render: (text: string) => <span data-dd-privacy="mask">{he.decode(text)}</span>,
        width: 150,
      },
      {
        key: 'noteLinks',
        title: 'Note Links',
        render: (_, record: TechnicianNote) => (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
            {record.link
              .filter(( link ) => link.link_type != NoteLinkType.Technician )
              .map(({ link_ID, link_type }) => (
                <Text.$ key={link_ID}>
                  {link_type === NoteLinkType.Job ? 'Job' : 'Customer'}:{' '}
                  <RouteLink to={getLinkUrl(link_ID, link_type)}>{link_ID}</RouteLink>
                </Text.$>
              ))}
          </Space.$>
        ),
        width: 150,
      },
      {
        key: 'noteType',
        title: 'Note Type',
        dataIndex: 'noteTypeName',
        width: 150,
      },
      {
        key: 'note',
        title: 'Note',
        dataIndex: 'notes',
      },
      {
        key: 'actions',
        title: 'Actions',
        render: (_, record) => (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
            <Link.$
              onClick={() => {
                setEditedNote(record);
                setShowNoteModal(true);
              }}
            >
              Edit
            </Link.$>
            <Popconfirm
              icon={false}
              title={<Text.$>Delete note</Text.$>}
              onConfirm={() => {
                deleteNote(record.ID);
              }}
              description={'Do you want to delete this note?'}
              okText="Yes"
            >
              <Link.$>
                <Text.$ type={'danger'}>Delete</Text.$>
              </Link.$>
            </Popconfirm>
          </Space.$>
        ),
        width: 80,
      },
    ];

    const paginationProps: TablePaginationConfig | false = notes?.meta
      ? {
          total: notes.meta.total ?? 0,
          current: page,
          pageSize: recordsPerPage,
          className: styles.edit__pagination,
          showTotal: (total) => (
            <Space.$ justify={Space.Justify.SpaceBetween} fullWidth>
              <Button.$
                type={Button.Type.Primary}
                onClick={() => setShowNoteModal(true)}
                dataTestId="create-new-ticket"
                icon={<PlusOutlined />}
              >
                New Note
              </Button.$>
              <span>Total {total} items</span>
            </Space.$>
          ),
          showSizeChanger: true,
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
          style: { marginTop: 24 },
        }
      : false;

    const handleNoteModal = (fields: TechnicianNoteModal.FormValues) => {
      const payload = {
        notes: fields.notes,
        note_type: Number(fields.note_type),
        notesLinkTypes: fields.notesLinkTypes?.map((value) => {
          const [link_type, link_ID] = value.split(':');
          return { link_type, link_ID } as unknown as TehcnicianNoteLink;
        }),
      };
      editedNote
        ? updateNote({ noteId: Number(fields.noteId), ...payload })
        : createNote({
            techId: Number(technicianId),
            ...payload,
          });
      handleModalClose();
    };

    const handleModalClose = () => {
      setShowNoteModal(false);
      setEditedNote(null);
    };

    return (
      <Flex vertical gap={24}>
        <Space.$ align={Space.Align.Center} style={{ paddingTop: 8 }}>
          <Text.$>Note Type: </Text.$>
          <Select
            value={noteType}
            onChange={(val) => setNoteType(val)}
            style={{ width: 210 }}
            options={[
              { label: 'All', value: '' },
              ...(noteTypes ?? []).map((type) => ({
                label: type?.name,
                value: type?.ID?.toString(),
              })),
            ]}
          />
        </Space.$>
        <Table
          loading={isLoading}
          rowKey={(record) => record.ID}
          columns={columns}
          dataSource={notes?.data ?? []}
          pagination={paginationProps}
          locale={{
            emptyText: 'No notes available.',
          }}
        />
        <TechnicianNoteModal.$
          note={editedNote}
          isOpen={showNoteModal}
          isLoading={!!isLoading}
          linkOptions={linkOptions}
          types={noteTypes}
          onSubmit={handleNoteModal}
          onClose={handleModalClose}
        />
      </Flex>
    );
  };
}
